import { type DocumentVersionSummary } from "~/api/types";

interface DocumentLink {
  parentDocumentId: number;
  childDocumentId: number;
}

const sortVersion = (a: DocumentVersionSummary, b: DocumentVersionSummary) => {
  const aOrder = a.document.chartOrder;
  const bOrder = b.document.chartOrder;

  if (aOrder !== null && bOrder !== null) {
    return aOrder - bOrder;
  }

  // Documents with a chartOrder come before those without
  if (aOrder !== null) return -1;
  if (bOrder !== null) return 1;

  return a.title.localeCompare(b.title);
};

type DocumentId = number;
type DocumentVersionMap = Map<DocumentId, DocumentVersionSummary>;
export type ChartColumn = Map<number, DocumentVersionSummary[]>;

export const getChartData = (
  publishedVersions: DocumentVersionSummary[],
): ChartColumn[] => {
  const { documentVersionMap, links } = extractData(publishedVersions);

  // Filter relationship arrays to only include documents visible on the chart.
  documentVersionMap.forEach((version) => {
    version.document = {
      ...version.document,
      parentDocumentIds: version.document.parentDocumentIds.filter((id) =>
        documentVersionMap.has(id),
      ),
      childDocumentIds: version.document.childDocumentIds.filter((id) =>
        documentVersionMap.has(id),
      ),
    };
  });

  const graphs = createGraphs(documentVersionMap, links);
  return getColumns(documentVersionMap, graphs);
};

const getColumns = (
  documentVersionMap: DocumentVersionMap,
  graphs: Graph[],
): ChartColumn[] => {
  const columns: ChartColumn[] = [];
  const addedIds = new Set<DocumentId>();

  graphs.forEach((graph) => {
    const column = new Map<number, DocumentVersionSummary[]>();

    graph.documentVersions
      .filter((v) => v.document.parentDocumentIds.length === 0)
      .forEach((v) => {
        recursivelyAddDocument(v, column, documentVersionMap, addedIds);
      });

    columns.push(column);
  });

  // Group all unconnected documents into a single column by tier
  const unconnectedColumn = new Map<number, DocumentVersionSummary[]>();

  documentVersionMap.forEach((version) => {
    if (!addedIds.has(version.document.id)) {
      const tierDocuments = [
        ...(unconnectedColumn.get(version.document.tier.id) ?? []),
        version,
      ];
      unconnectedColumn.set(version.document.tier.id, tierDocuments);
    }
  });

  if (unconnectedColumn.size > 0) {
    columns.push(unconnectedColumn);
  }

  // Sort the documents in each column by chartOrder / title
  columns.forEach((column) => {
    column.forEach((documents) => documents.sort(sortVersion));
  });

  return columns;
};

const recursivelyAddDocument = (
  version: DocumentVersionSummary,
  column: ChartColumn,
  documents: DocumentVersionMap,
  addedIds: Set<DocumentId>,
) => {
  addedIds.add(version.document.id);
  const tierDocuments = [
    ...(column.get(version.document.tier.id) ?? []),
    version,
  ];
  column.set(version.document.tier.id, tierDocuments);

  version.document.childDocumentIds.forEach((childId) => {
    const childDocument = documents.get(childId);
    // If a child document has no published version, we don't want to add it to the chart
    if (childDocument && !addedIds.has(childId)) {
      recursivelyAddDocument(childDocument, column, documents, addedIds);
    }
  });
};

interface Graph {
  documentVersions: DocumentVersionSummary[];
}

// Find all connected documents (across categories)
const createGraphs = (
  documentVersionMap: DocumentVersionMap,
  links: DocumentLink[],
) => {
  const graphs: Graph[] = [];
  const documentGraphMap = new Map<DocumentId, Graph>();

  for (const { parentDocumentId, childDocumentId } of links) {
    const parentVersion = documentVersionMap.get(parentDocumentId);
    const childVersion = documentVersionMap.get(childDocumentId);

    if (!parentVersion || !childVersion) {
      // this could happen if a link was created for a draft document
      continue;
    }

    const parentGraph = documentGraphMap.get(parentDocumentId);
    const childGraph = documentGraphMap.get(childDocumentId);

    if (!parentGraph && !childGraph) {
      const graph: Graph = { documentVersions: [parentVersion, childVersion] };
      graphs.push(graph);
      documentGraphMap.set(parentDocumentId, graph);
      documentGraphMap.set(childDocumentId, graph);
    } else if (parentGraph && !childGraph) {
      parentGraph.documentVersions.push(childVersion);
      documentGraphMap.set(childDocumentId, parentGraph);
    } else if (!parentGraph && childGraph) {
      childGraph.documentVersions.push(parentVersion);
      documentGraphMap.set(parentDocumentId, childGraph);
    } else if (parentGraph && childGraph && parentGraph !== childGraph) {
      for (const version of childGraph.documentVersions) {
        documentGraphMap.set(version.document.id, parentGraph);
      }
      parentGraph.documentVersions.push(...childGraph.documentVersions);
      graphs.splice(graphs.indexOf(childGraph), 1);
    }
  }

  return graphs;
};

// Add all documents to a map and extract all links between documents for easy access
const extractData = (versions: DocumentVersionSummary[]) => {
  const documentVersionMap = new Map<DocumentId, DocumentVersionSummary>();
  const links: DocumentLink[] = [];

  versions.forEach((d) => {
    // Shallow copy to avoid mutating SWR cache data when filtering document relations
    documentVersionMap.set(d.document.id, { ...d });

    d.document.childDocumentIds.forEach((childId) => {
      links.push({
        parentDocumentId: d.document.id,
        childDocumentId: childId,
      });
    });
  });

  return { documentVersionMap, links };
};
